import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatAUD, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload } from "@/components/ui-helpers";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { RowActions } from "@/components/RowActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, CreditCard, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { maybeSuggestStatus } from "@/lib/statusSuggest";

export default function Purchases() {
  const { trips, selectedTripId } = useApp();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [form, setForm] = useState({ trip_id: "", product_id: "", product_name: "", store: "", actual_aud_price: "", discount_received: "0", quantity: 1, receipt: null, notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        api.get("/purchases", { params: { trip_id: selectedTripId, include_deleted: isAdmin && showDeleted } }),
        api.get("/products", { params: { trip_id: selectedTripId } }),
      ]);
      setItems(p.data); setProducts(pr.data.items);
    } finally { setLoading(false); }
  }, [selectedTripId, isAdmin, showDeleted]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ trip_id: selectedTripId !== "all" ? selectedTripId : (trips[0]?.id || ""), product_id: "", product_name: "", store: "", actual_aud_price: "", discount_received: "0", quantity: 1, receipt: null, notes: "" }); setOpen(true); };

  const save = async () => {
    if (!form.trip_id) return toast.error("Pilih trip");
    try { const res = await api.post("/purchases", { ...form, quantity: Number(form.quantity) }); toast.success("Pembelian dicatat"); setOpen(false); load(); maybeSuggestStatus(res.data, load); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doArchive = async () => {
    await api.delete(`/purchases/${archiveTarget.id}`);
    toast.success("Pembelian diarsipkan"); load();
  };

  return (
    <div>
      <PageHeader title="Pembelian" subtitle="Catat pembelian aktual & bandingkan dengan estimasi">
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer" data-testid="purchase-show-deleted">
            <Checkbox checked={showDeleted} onCheckedChange={setShowDeleted} />Tampilkan Terhapus
          </label>
        )}
        <Button onClick={openNew} data-testid="purchase-create-button"><Plus className="h-4 w-4 mr-1.5" />Catat Pembelian</Button>
      </PageHeader>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState title="Belum ada pembelian" action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Catat Pembelian</Button>} />
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className={`rounded-xl border border-border bg-card p-4 shadow-sm ${p.deleted ? "opacity-60 bg-secondary/30" : ""}`} data-testid="purchase-card">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-medium">{p.product_name || "(tanpa nama)"}{p.deleted && <span className="ml-2 text-xs text-destructive">(Terarsip)</span>}</p><p className="text-xs text-muted-foreground">{p.store} · {formatDate(p.purchase_date)} · {p.quantity} pcs</p></div>
                <div className="flex items-start gap-2">
                  <p className="font-mono font-semibold">{formatIDR(p.actual_total_idr)}</p>
                  {isAdmin && !p.deleted && <RowActions onArchive={() => setArchiveTarget(p)} testId="purchase" />}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Aktual AUD</p><p className="font-mono">{formatAUD(p.actual_total_aud)}</p></div>
                <div><p className="text-xs text-muted-foreground">Estimasi (Rp)</p><p className="font-mono">{formatIDR(p.estimated_idr)}</p></div>
                <div><p className="text-xs text-muted-foreground">Selisih</p><p className={`font-mono flex items-center gap-1 ${p.difference_idr >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{p.difference_idr >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{formatIDR(p.difference_idr)}</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Catat Pembelian</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Trip" required>
              <SearchableSelect
                value={form.trip_id}
                onChange={(v) => set("trip_id", v)}
                options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
                placeholder="Pilih trip"
                searchPlaceholder="Cari trip..."
                emptyText="Trip tidak ditemukan"
                testId="purchase-trip-select"
              />
            </Field>
            <Field label="Produk">
              <SearchableSelect
                showImages
                value={form.product_id}
                onChange={(v) => { const p = products.find((x) => x.id === v); set("product_id", v); set("product_name", p?.name || ""); set("actual_aud_price", p?.aud_price || ""); }}
                options={products.filter((p) => p.trip_id === form.trip_id).map((p) => ({
                  value: p.id,
                  label: p.name,
                  sublabel: [p.product_code, p.brand, p.category, p.variant].filter(Boolean).join(" · "),
                  image: p.thumb_url || p.photo || null,
                  keywords: [p.name, p.brand, p.category, p.variant, p.size, p.colour, p.sku, p.product_code],
                }))}
                placeholder="Pilih produk"
                searchPlaceholder="Cari nama / brand / SKU..."
                emptyText="Produk tidak ditemukan"
                testId="purchase-product-select"
              />
            </Field>
            <Field label="Nama Produk"><Input value={form.product_name} onChange={(e) => set("product_name", e.target.value)} /></Field>
            <Field label="Toko Aktual"><Input value={form.store} onChange={(e) => set("store", e.target.value)} /></Field>
            <Field label="Harga Aktual AUD" required><Input type="number" step="0.01" value={form.actual_aud_price} onChange={(e) => set("actual_aud_price", e.target.value)} data-testid="purchase-aud-input" /></Field>
            <Field label="Diskon Diterima AUD"><Input type="number" step="0.01" value={form.discount_received} onChange={(e) => set("discount_received", e.target.value)} /></Field>
            <Field label="Qty"><Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
          </div>
          <ImageUpload value={form.receipt} onChange={(b) => set("receipt", b)} label="Struk" testId="purchase-receipt-upload" />
          <Field label="Catatan"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="purchase-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        title="Arsipkan Pembelian?"
        description={`Pembelian "${archiveTarget?.product_name || ""}" akan diarsipkan dan disembunyikan dari daftar. Dapat dipulihkan dari Kotak Sampah.`}
        onConfirm={doArchive} testId="purchase-archive" />
    </div>
  );
}
